/**
 * @file Navbar.js
 * @author Stanley Chen
 * @description Navigation bar component that appears at the top of every page with
 * links to different sections of the website and a mobile-responsive design
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import OutboundLink from '../OutboundLink';
import './Navbar.css';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showBottomNav, setShowBottomNav] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';
  const isMenuPage = !isHomePage
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
  
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (isHomePage) {
        // On homepage: show bottom nav only when scrolled past landing page
        if (currentScrollY > window.innerHeight * 0.7) {
          setScrolled(true);
          setShowBottomNav(true);
        } else {
          setScrolled(currentScrollY > 50);
          setShowBottomNav(false);
        }
      } else {
        setScrolled(currentScrollY > 50);
        setShowBottomNav(true);
      }
    };
  
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    if (!isHomePage) {
      setShowBottomNav(true);
    } else {
      handleScroll(); 
    }
  
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setDropdownOpen(false); 
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  return (
    <>
      <motion.nav 
        className={`navbar ${scrolled ? 'scrolled' : ''} ${isMenuPage ? 'menu-page-nav' : ''}`}
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="navbar-container">
          <div className="logo">
            <Link to="/">
              <img src="/images/kiku-logo-small.png" alt="Kiku Japanese Steakhouse" />
            </Link>
          </div>

          <div className={`mobile-icon ${isMenuPage ? 'menu-page-icon' : ''}`} onClick={toggleMobileMenu}>
            <div className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}></div>
          </div>

          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''} ${isMenuPage ? 'menu-page-links' : ''}`}>
            <li className="nav-item">
              <Link to="/menu" onClick={() => setMobileMenuOpen(false)}>Menu</Link>
            </li>
            <li className="nav-item">
              <Link to="/drinks" onClick={() => setMobileMenuOpen(false)}>Drinks</Link>
            </li>

            {isMobile ? (
              <>
                <li className="nav-item">
                  <OutboundLink
                    href="https://kikude.kwickmenu.com/"
                    label="Online Ordering - Pickup"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Pickup
                  </OutboundLink>
                </li>
                <li className="nav-item">
                  <OutboundLink
                    href="https://www.doordash.com/store/kiku-japanese-steakhouse-&-sushi-bar-middletown-579754/1499137/"
                    label="Online Ordering - DoorDash"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Delivery
                  </OutboundLink>
                </li>
              </>
            ) : (
              <li className="nav-item dropdown">
                <button onClick={toggleDropdown} className="dropdown-toggle">
                  Order <span className={`arrow ${dropdownOpen ? 'open' : ''}`}>&#9662;</span>
                </button>
                {dropdownOpen && (
                  <ul className="dropdown-menu">
                    <li>
                      <OutboundLink
                        href="https://kikude.kwickmenu.com/"
                        label="Online Ordering - Pickup"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setDropdownOpen(false);
                        }}
                      >
                        Pickup
                      </OutboundLink>
                    </li>
                    <li>
                      <OutboundLink
                        href="https://www.doordash.com/store/kiku-japanese-steakhouse-&-sushi-bar-middletown-579754/1499137/"
                        label="Online Ordering - DoorDash"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setDropdownOpen(false);
                        }}
                      >
                        Delivery
                      </OutboundLink>
                    </li>
                  </ul>
                )}
              </li>
            )}

            <li className="nav-item">
              <OutboundLink
                href="https://www.myhibachiparty.com/"
                label="Catering Website"
                onClick={() => setMobileMenuOpen(false)}
              >
                Catering
              </OutboundLink>
            </li>
            <li className="nav-item">
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)}>Gallery</Link>
            </li>
            <li className="nav-item">
              <Link to="/contact-us" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            </li>
            
          </ul>
        </div>
      </motion.nav>

      {isMobile && (
        <div className={`mobile-bottom-nav ${showBottomNav ? 'visible' : ''}`}>
          <OutboundLink 
            href="https://kikude.kwickmenu.com/" 
            className="mobile-nav-button pickup"
            label="Online Ordering - Pickup (Mobile)"
          >
            Pickup
          </OutboundLink>
          <OutboundLink 
            href="https://www.doordash.com/store/kiku-japanese-steakhouse-&-sushi-bar-middletown-579754/1499137/" 
            className="mobile-nav-button doordash"
            label="Online Ordering - DoorDash (Mobile)"
          >
            Doordash
          </OutboundLink>
        </div>
      )}
    </>
  );
};

export default Navbar;